import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import jwt_decode from 'jwt-decode';
import { LoginSuccessResponse } from './../responses/login-success.response';

import { Observable, ReplaySubject } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';

import { environment } from 'src/environments/environment';
import { SessionErrorsManager } from '~modules/errors-management/services/session-errors-manager.service';
import { JwtUser } from '../entities/jwt-user.entity';
import { LoginData } from '../entities/login-data.entity';
import { User } from '../entities/user.entity';

@Injectable()
export class AuthenticationService {

    private currentUserSubject: ReplaySubject<User|null>;

    constructor(
        private http: HttpClient,
        private router: Router,
        private readonly sessionErrorsManager: SessionErrorsManager,
    ) {
        this.currentUserSubject = new ReplaySubject<User|null>(1);
        this.currentUserSubject.next(this.getCurrentUserFromLocalStorage());
    }

    getCurrentUserFromLocalStorage(): User|null {
      const localStorageValue: string | null = localStorage.getItem('currentUser');
      if (localStorageValue == null) {
        return null;
      }
      const user: User = JSON.parse(localStorageValue);
      if (user && user.expirationDate) {
          user.expirationDate = new Date(user.expirationDate);
      }
      return user;
    }

    /**
     * Get the current user.
     * If the user token is expired, then the application display an error message, clear localStorage and redirect to the 'sign-in' page.
     */
    get currentUser$(): Observable<User|null> {
        return this.currentUserSubject
            .pipe(
                tap(user => {
                    if (user != null && user.expirationDate <= new Date()) {
                        this.sessionErrorsManager.add({
                            msg: 'Token expired, please reconnect'
                        });
                        this.currentUserSubject.next(null);
                        localStorage.removeItem('currentUser');
                        this.router.navigate(['/auth/sign-in'], { queryParams: { returnUrl: window.location.pathname } });
                        throw new Error('user token has expired');
                    }
                })
            );
    }

    loginSuccess(result: LoginSuccessResponse): User {
        const token = `${result.tokenType} ${result.accessToken}`;
        const jwtUser: JwtUser = jwt_decode(result.accessToken);
        const user: User = {
            id: jwtUser.user.id,
            email: jwtUser.user.email,
            firstName: jwtUser.user.firstName,
            lastName: jwtUser.user.lastName,
            token,
            expirationDate: new Date(jwtUser.exp * 1000) // Date have epoch constructor in milliseconds, and jwt.exp is in seconds
        };
        this.currentUserSubject.next(user);
        localStorage.setItem('currentUser', JSON.stringify(user));
        return user;
    }

    signIn$(loginData: LoginData): Observable<User> {
        const url = `${environment.apiURL}/auth/sign-in`;
        return this.http
            .post<LoginSuccessResponse>(url, loginData)
            .pipe(
                take(1),
                map((result) => this.loginSuccess(result))
            ) ;
    }

    async signOut(): Promise<void> {
        this.currentUserSubject.next(null);
        localStorage.removeItem('currentUser');
        await this.router.navigate(['/auth/sign-in']);
    }

    /**
     * Check if the user is connected and if he has an unexpired token.
     * If the token is expired, the localstorage is cleared.
     */
    isConnected$(): Observable<boolean> {
        return this.currentUserSubject
            .pipe(
                map((user: User|null) => {
                    if (
                        user !== null
                        && user.token
                        && user.token !== ''
                    ) {
                        if (user.expirationDate > new Date()) {
                            return true;
                        } else {
                            this.currentUserSubject.next(null);
                            localStorage.removeItem('currentUser');
                            return false;
                        }
                    }
                    return false;
                })
            );
    }

    changeUserPassword(oldPassword: string, newPassword: string): Observable<void> {
        const url = `${environment.apiURL}/auth/update-password`;
        return this.http
            .put<void>(url, {
                oldPassword,
                newPassword
            });
    }

    updateUserConnections$() : Observable<void> {
        const url = `${environment.apiURL}/auth/current/connection-update`;
        return this.http.patch<void>(url, null);
      }
}
